import React, { useState, useEffect } from 'react';
import { GraduationCap, LogOut, Calendar, Users, Upload, Edit2, Clock, BookOpen, Globe, CheckCircle, AlertCircle } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';

const BACKEND_URL = '';

export default function TutorDashboard() {
  const { user, logout } = useAuth();
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [myProfile, setMyProfile] = useState(null);
  const [showProfileForm, setShowProfileForm] = useState(false);
  const [profileForm, setProfileForm] = useState({ name: '', subjects: '', languages: '', bio: '', years_experience: '' });
  const [savingProfile, setSavingProfile] = useState(false);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [sessionsRes, profileRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/sessions`, { withCredentials: true }),
        axios.get(`${BACKEND_URL}/api/tutor-profiles/my`, { withCredentials: true }).catch(() => ({ data: null }))
      ]);
      setSessions(sessionsRes.data);
      setMyProfile(profileRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const updateSessionStatus = async (sessionId, status) => {
    try {
      await axios.patch(`${BACKEND_URL}/api/sessions/${sessionId}`, { status }, { withCredentials: true });
      fetchData();
    } catch (error) {
      console.error('Error updating session:', error);
      alert('Failed to update session');
    }
  };

  const handleEditProfile = () => {
    if (myProfile) {
      setProfileForm({
        name: myProfile.name || '',
        subjects: myProfile.subjects?.join(', ') || '',
        languages: myProfile.languages?.join(', ') || '',
        bio: myProfile.bio || '',
        years_experience: String(myProfile.years_experience || '')
      });
    } else {
      setProfileForm({ name: user.name || '', subjects: '', languages: '', bio: '', years_experience: '' });
    }
    setShowProfileForm(true);
  };

  const handleSaveProfile = async () => {
    setSavingProfile(true);
    try {
      const payload = {
        name: profileForm.name,
        subjects: profileForm.subjects.split(',').map(s => s.trim()).filter(Boolean),
        languages: profileForm.languages.split(',').map(s => s.trim()).filter(Boolean),
        bio: profileForm.bio,
        years_experience: parseInt(profileForm.years_experience) || 0
      };

      if (myProfile) {
        await axios.put(`${BACKEND_URL}/api/tutor-profiles/${myProfile.profile_id}`, payload, { withCredentials: true });
      } else {
        await axios.post(`${BACKEND_URL}/api/tutor-profiles`, payload, { withCredentials: true });
      }
      setShowProfileForm(false);
      fetchData();
    } catch (error) {
      console.error('Error saving profile:', error);
      alert(error.response?.data?.detail || 'Failed to save profile');
    } finally {
      setSavingProfile(false);
    }
  };

  const handlePhotoUpload = async (file) => {
    if (!myProfile) return;
    setUploadingPhoto(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      await axios.post(`${BACKEND_URL}/api/tutor-profiles/${myProfile.profile_id}/photo`, formData, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      fetchData();
    } catch (error) {
      console.error('Error uploading photo:', error);
      alert(error.response?.data?.detail || 'Failed to upload photo');
    } finally {
      setUploadingPhoto(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-lg">Loading...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F3F4F6]">
      <nav className="bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <img 
                src="https://customer-assets.emergentagent.com/job_elite-tutoring-1/artifacts/0zn1n7em_1000075255.png" 
                alt="ESL Elite Logo" 
                className="h-10 w-auto"
              />
            </div>
            <div className="flex items-center gap-4">
              <span className="text-sm font-semibold">Welcome, {user.name}</span>
              <button
                onClick={logout}
                className="flex items-center gap-2 text-sm font-semibold px-4 py-2 border border-slate-900 rounded-md hover:bg-slate-900 hover:text-white"
                data-testid="logout-button"
              >
                <LogOut className="w-4 h-4" /> Logout
              </button>
            </div>
          </div>
        </div>
      </nav>

      <div className="max-w-7xl mx-auto px-6 py-12">
        <div className="mb-8">
          <h1 className="text-3xl font-black tracking-tight mb-2">Tutor Dashboard</h1>
          <p className="text-slate-600">Manage your teaching schedule and profile</p>
        </div>

        {/* My Profile Section */}
        <div className="bg-white border border-slate-200 p-6 mb-8">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-bold">My Public Profile</h2>
            <button
              onClick={handleEditProfile}
              className="flex items-center gap-2 px-4 py-2 bg-[#002FA7] text-white font-semibold rounded-md hover:bg-[#001a5c] text-sm"
              data-testid="edit-my-profile-btn"
            >
              {myProfile ? <><Edit2 className="w-4 h-4" /> Edit Profile</> : <><GraduationCap className="w-4 h-4" /> Create Profile</>}
            </button>
          </div>

          {myProfile ? (
            <div className="flex gap-6">
              <div className="w-32 h-32 bg-slate-100 rounded-md overflow-hidden flex-shrink-0 relative">
                {myProfile.photo_url ? (
                  <img src={myProfile.photo_url} alt={myProfile.name} className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-[#002FA7]/10 to-[#002FA7]/5">
                    <span className="text-3xl font-black text-[#002FA7]/20">{myProfile.name?.charAt(0)}</span>
                  </div>
                )}
                <label className="absolute bottom-1 right-1 cursor-pointer bg-white/90 px-2 py-1 rounded text-xs font-semibold flex items-center gap-1 hover:bg-white">
                  <Upload className="w-3 h-3" />
                  {uploadingPhoto ? '...' : 'Photo'}
                  <input
                    type="file"
                    accept="image/jpeg,image/png,image/webp"
                    className="hidden"
                    onChange={(e) => { if (e.target.files[0]) handlePhotoUpload(e.target.files[0]); }}
                    data-testid="upload-my-photo"
                  />
                </label>
              </div>
              <div>
                <h3 className="text-lg font-bold mb-1">{myProfile.name}</h3>
                <div className="flex items-center gap-2 mb-2">
                  {myProfile.status === 'approved' ? (
                    <span className="inline-flex items-center gap-1 px-2 py-1 bg-green-100 text-green-800 text-xs font-semibold rounded">
                      <CheckCircle className="w-3 h-3" /> Approved - Visible to public
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1 px-2 py-1 bg-yellow-100 text-yellow-800 text-xs font-semibold rounded">
                      <AlertCircle className="w-3 h-3" /> Pending Admin Approval
                    </span>
                  )}
                </div>
                <div className="flex flex-wrap gap-1 mb-2">
                  {myProfile.subjects?.map((s, i) => (
                    <span key={i} className="inline-flex items-center gap-1 px-2 py-0.5 bg-[#002FA7]/10 text-[#002FA7] text-xs font-semibold rounded">
                      <BookOpen className="w-3 h-3" /> {s}
                    </span>
                  ))}
                </div>
                <div className="flex flex-wrap gap-1 mb-2">
                  {myProfile.languages?.map((l, i) => (
                    <span key={i} className="inline-flex items-center gap-1 px-2 py-0.5 bg-[#E32636]/10 text-[#E32636] text-xs font-semibold rounded">
                      <Globe className="w-3 h-3" /> {l}
                    </span>
                  ))}
                </div>
                <div className="flex items-center gap-1 text-sm text-slate-500 mb-2">
                  <Clock className="w-4 h-4" /> {myProfile.years_experience}+ years experience
                </div>
                <p className="text-sm text-slate-600">{myProfile.bio}</p>
              </div>
            </div>
          ) : (
            <div className="text-center py-6">
              <GraduationCap className="w-10 h-10 text-slate-300 mx-auto mb-3" />
              <p className="text-slate-600 mb-1">You haven't created your public profile yet</p>
              <p className="text-sm text-slate-400">Create your profile so students can find you on the website</p>
            </div>
          )}
        </div>

        {/* Profile Form Modal */}
        {showProfileForm && (
          <div className="bg-white border border-slate-200 p-6 mb-8">
            <h3 className="text-lg font-bold mb-4">{myProfile ? 'Edit Your Profile' : 'Create Your Profile'}</h3>
            {myProfile && (
              <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded-md text-sm text-yellow-800">
                Note: Editing your profile will require admin re-approval before it becomes visible to the public.
              </div>
            )}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-semibold mb-1">Full Name</label>
                <input
                  type="text"
                  value={profileForm.name}
                  onChange={(e) => setProfileForm({ ...profileForm, name: e.target.value })}
                  className="w-full px-4 py-2 border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-[#002FA7]"
                  data-testid="tutor-profile-name"
                />
              </div>
              <div>
                <label className="block text-sm font-semibold mb-1">Years of Experience</label>
                <input
                  type="number"
                  value={profileForm.years_experience}
                  onChange={(e) => setProfileForm({ ...profileForm, years_experience: e.target.value })}
                  className="w-full px-4 py-2 border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-[#002FA7]"
                  data-testid="tutor-profile-experience"
                />
              </div>
              <div>
                <label className="block text-sm font-semibold mb-1">Subjects (comma-separated)</label>
                <input
                  type="text"
                  value={profileForm.subjects}
                  onChange={(e) => setProfileForm({ ...profileForm, subjects: e.target.value })}
                  className="w-full px-4 py-2 border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-[#002FA7]"
                  placeholder="e.g. French, English, Math"
                  data-testid="tutor-profile-subjects"
                />
              </div>
              <div>
                <label className="block text-sm font-semibold mb-1">Languages (comma-separated)</label>
                <input
                  type="text"
                  value={profileForm.languages}
                  onChange={(e) => setProfileForm({ ...profileForm, languages: e.target.value })}
                  className="w-full px-4 py-2 border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-[#002FA7]"
                  placeholder="e.g. French, English, Creole"
                  data-testid="tutor-profile-languages"
                />
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm font-semibold mb-1">Bio</label>
                <textarea
                  value={profileForm.bio}
                  onChange={(e) => setProfileForm({ ...profileForm, bio: e.target.value })}
                  rows={3}
                  className="w-full px-4 py-2 border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-[#002FA7]"
                  placeholder="Tell students about yourself, your experience, and teaching style..."
                  data-testid="tutor-profile-bio"
                />
              </div>
            </div>
            <div className="mt-4 flex gap-3">
              <button
                onClick={handleSaveProfile}
                disabled={savingProfile || !profileForm.name}
                className="px-6 py-2 bg-[#002FA7] text-white font-semibold rounded-md hover:bg-[#001a5c] disabled:opacity-50"
                data-testid="save-tutor-profile-btn"
              >
                {savingProfile ? 'Saving...' : myProfile ? 'Update & Submit for Approval' : 'Submit Profile'}
              </button>
              <button
                onClick={() => setShowProfileForm(false)}
                className="px-6 py-2 border border-slate-300 text-slate-700 font-semibold rounded-md hover:bg-slate-50"
              >
                Cancel
              </button>
            </div>
          </div>
        )}

        {/* Stats */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
          <div className="bg-white p-6 border border-slate-200">
            <div className="text-sm font-semibold tracking-[0.2em] uppercase text-slate-600 mb-2">Total Sessions</div>
            <div className="text-3xl font-black">{sessions.length}</div>
          </div>
          <div className="bg-white p-6 border border-slate-200">
            <div className="text-sm font-semibold tracking-[0.2em] uppercase text-slate-600 mb-2">Pending</div>
            <div className="text-3xl font-black">{sessions.filter(s => s.status === 'pending').length}</div>
          </div>
          <div className="bg-white p-6 border border-slate-200">
            <div className="text-sm font-semibold tracking-[0.2em] uppercase text-slate-600 mb-2">Confirmed</div>
            <div className="text-3xl font-black">{sessions.filter(s => s.status === 'confirmed').length}</div>
          </div>
        </div>

        {/* Sessions Table */}
        <div className="bg-white border border-slate-200">
          <div className="p-6 border-b border-slate-200">
            <h2 className="text-xl font-bold">Upcoming Sessions</h2>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-[#F3F4F6]">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-semibold tracking-[0.2em] uppercase">Subject</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold tracking-[0.2em] uppercase">Date</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold tracking-[0.2em] uppercase">Time</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold tracking-[0.2em] uppercase">Duration</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold tracking-[0.2em] uppercase">Status</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold tracking-[0.2em] uppercase">Actions</th>
                </tr>
              </thead>
              <tbody>
                {sessions.length === 0 ? (
                  <tr>
                    <td colSpan="6" className="px-6 py-8 text-center text-slate-600">
                      No sessions scheduled yet
                    </td>
                  </tr>
                ) : (
                  sessions.map(session => (
                    <tr key={session.id} className="border-b border-slate-200">
                      <td className="px-6 py-4">{session.subject}</td>
                      <td className="px-6 py-4">{session.date}</td>
                      <td className="px-6 py-4">{session.time}</td>
                      <td className="px-6 py-4">{session.duration} min</td>
                      <td className="px-6 py-4">
                        <span className={`inline-block px-3 py-1 text-xs font-semibold rounded ${
                          session.status === 'confirmed' ? 'bg-green-100 text-green-800' :
                          session.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                          'bg-slate-100 text-slate-800'
                        }`}>
                          {session.status}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        {session.status === 'pending' && session.paid && (
                          <button
                            onClick={() => updateSessionStatus(session.id, 'confirmed')}
                            className="text-sm font-semibold text-[#002FA7] hover:underline mr-4"
                            data-testid="confirm-session-button"
                          >
                            Confirm
                          </button>
                        )}
                        {session.status === 'pending' && (
                          <button
                            onClick={() => updateSessionStatus(session.id, 'cancelled')}
                            className="text-sm font-semibold text-[#E32636] hover:underline"
                          >
                            Decline
                          </button>
                        )}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}
